/**
 * Shared time / duration formatters used across hub-ui and cloud-ui.
 *
 * Hub-ui's `lib/format.ts` re-exports from here so both web apps display
 * timings identically. Add per-app helpers (e.g. server-address formatters)
 * to the consuming app rather than this package.
 */

/** Format milliseconds to a countdown display, e.g. "5:23" or "1:05:23". */
export function formatTimeLeft(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** Format an ISO-8601 string to a locale display string (e.g. for createdAt). */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

/** Format a lap time in milliseconds to "M:SS.mmm". Used on lap-time tables
 *  and the leaderboard tile across both web apps. */
export function formatLapTime(ms: number): string {
  if (!ms || ms <= 0) return "—";
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = ms % 1000;
  return `${minutes}:${seconds.toString().padStart(2, "0")}.${millis
    .toString()
    .padStart(3, "0")}`;
}

/** Format seconds to a human-readable duration (e.g. "5m 30s"). */
export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
}
